import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";

import type {
  PolicyAlert,
  PolicyGuardDashboard,
  PolicyGuardScanResult,
} from "../types/policy-guard.types";

export const policyGuardKeys = {
  all: ['policyGuard'] as const,
  alerts: () => [...policyGuardKeys.all, 'alerts'] as const,
  dashboard: () => [...policyGuardKeys.all, 'dashboard'] as const,
  leadAlerts: (leadId: string) => [...policyGuardKeys.all, 'lead', leadId] as const,
};

/**
 * Trigger a full policy risk scan.
 * Returns fresh alerts and stats.
 */
export const usePolicyGuardScan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<PolicyGuardScanResult> => {
      const response = await api.post({
        url: getApiUrl(endpoint.policyGuard.scan),
        body: {},
        auth: true,
      });
      return (response as any)?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyGuardKeys.all });
    },
  });
};

/**
 * Get all active alerts (cached, requires scan to be run first).
 */
export const usePolicyGuardAlerts = (options?: { riskLevel?: string; enabled?: boolean }) => {
  return useQuery<PolicyAlert[]>({
    queryKey: [...policyGuardKeys.alerts(), options?.riskLevel],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.riskLevel) params.set('riskLevel', options.riskLevel);
      const url = getApiUrl(endpoint.policyGuard.getAlerts) + (params.toString() ? `?${params}` : '');
      const response = await api.get({ url, auth: true });
      return (response as any)?.data || [];
    },
    enabled: options?.enabled !== false,
    staleTime: 60 * 1000, // 1 min
    refetchOnMount: true,
  });
};

/**
 * Get dashboard stats for the PolicyGuard widget.
 */
export const usePolicyGuardDashboard = (enabled = true) => {
  return useQuery<PolicyGuardDashboard>({
    queryKey: policyGuardKeys.dashboard(),
    queryFn: async () => {
      const response = await api.get({
        url: getApiUrl(endpoint.policyGuard.getDashboard),
        auth: true,
      });
      return (response as any)?.data || {
        critical: 0,
        atRisk: 0,
        watch: 0,
        healthy: 0,
        totalAlerts: 0,
        totalPremiumAtRisk: 0,
        totalPolicies: 0,
        bookHealthScore: 100,
      };
    },
    enabled,
    staleTime: 60 * 1000,
    refetchOnMount: true,
  });
};

/**
 * Get alerts for a specific lead.
 */
export const usePolicyGuardLeadAlerts = (leadId: string) => {
  return useQuery<PolicyAlert[]>({
    queryKey: policyGuardKeys.leadAlerts(leadId),
    queryFn: async () => {
      const response = await api.get({
        url: getApiUrl(endpoint.policyGuard.getLeadAlerts(leadId)),
        auth: true,
      });
      return (response as any)?.data || [];
    },
    enabled: !!leadId,
    staleTime: 60 * 1000,
  });
};

/**
 * Update an alert's status (acknowledge, resolve, dismiss).
 */
export const useUpdatePolicyAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      alertId: string;
      status: 'acknowledged' | 'resolved' | 'dismissed';
      resolvedReason?: string;
    }) => {
      const response = await api.patch({
        url: getApiUrl(endpoint.policyGuard.updateAlert(params.alertId)),
        body: { status: params.status, resolvedReason: params.resolvedReason },
        auth: true,
      });
      return (response as any)?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyGuardKeys.all });
    },
  });
};
